import React, { useContext, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  deletePost,
  updatePost,
  getMediaUrl,
  likePost,
  unlikePost,
  getComments,
  addComment,
  deleteComment,
  repostPost,
  unrepostPost,
  bookmarkPost,
  unbookmarkPost,
} from '../services/api';
import ConnectModal from './ConnectModal';

function Post({ post, onDelete, onUpdate }) {
  const { user } = useContext(AuthContext);
  const [liked, setLiked] = useState(post.user_liked || false);
  const [likeCount, setLikeCount] = useState(post.like_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentCount, setCommentCount] = useState(post.comment_count || 0);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [reposted, setReposted] = useState(post.user_reposted || false);
  const [repostCount, setRepostCount] = useState(post.repost_count || 0);
  const [bookmarked, setBookmarked] = useState(post.user_bookmarked || false);
  const [lightbox, setLightbox] = useState(null);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  // SHO-33: edit state. optimisticEdit lets the new content + edited stamp
  // appear immediately after a successful PUT, before the parent's refresh
  // round-trip lands. The displayed values fall back to the original prop
  // until then.
  const [isEditing, setIsEditing] = useState(false);
  const [editDraft, setEditDraft] = useState('');
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [optimisticEdit, setOptimisticEdit] = useState(null);

  const displayContent = optimisticEdit?.content ?? post.content;
  const displayEditedAt = optimisticEdit?.edited_at ?? post.edited_at;
  // SHO-19/28: connection state derived from feed flags.
  // 'none'    → viewer hasn't connected yet (renders + Connect)
  // 'pending' → viewer requested but author hasn't reciprocated
  // 'mutual'  → both directions follow (button is hidden)
  const initialConnectionState = post.user_followed_author
    ? post.author_followed_user
      ? 'mutual'
      : 'pending'
    : 'none';
  const [connectionState, setConnectionState] = useState(initialConnectionState);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const commentsRef = useRef(null);
  const replyInputRef = useRef(null);

  // Close lightbox on Escape key
  useEffect(() => {
    if (!lightbox) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [lightbox]);

  const openLightbox = (mediaData) => {
    // Prevent double-fire by checking if already open
    if (lightbox) return;
    setLightbox(mediaData);
  };

  // Focus reply input when replying
  useEffect(() => {
    if (replyingTo && replyInputRef.current) {
      replyInputRef.current.focus();
    }
  }, [replyingTo]);

  const loadComments = useCallback(async () => {
    try {
      const res = await getComments(post.id);
      const data = res.data || [];
      setComments(data);
      setCommentCount(data.length);
      setCommentsLoaded(true);
    } catch (err) {
      console.error('Error loading comments:', err);
    }
  }, [post.id]);

  const handleToggleComments = () => {
    if (!showComments && !commentsLoaded) {
      loadComments();
    }
    setShowComments(!showComments);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await deletePost(post.id);
        if (onDelete) onDelete();
      } catch (error) {
        console.error('Error deleting post:', error);
      }
    }
  };

  const handleStartEdit = () => {
    setEditDraft(displayContent);
    setEditError('');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditDraft('');
    setEditError('');
  };

  const handleSubmitEdit = async () => {
    const trimmed = editDraft.trim();
    if (!trimmed) {
      setEditError('Content is required.');
      return;
    }
    if (trimmed === displayContent) {
      handleCancelEdit();
      return;
    }
    setEditSaving(true);
    try {
      await updatePost(post.id, { content: trimmed, post_type: post.post_type });
      setOptimisticEdit({ content: trimmed, edited_at: new Date().toISOString() });
      setIsEditing(false);
      setEditDraft('');
      setEditError('');
      if (onUpdate) onUpdate();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to save changes.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleLike = async () => {
    try {
      if (liked) {
        setLiked(false);
        setLikeCount((prev) => prev - 1);
        await unlikePost(post.id);
      } else {
        setLiked(true);
        setLikeCount((prev) => prev + 1);
        await likePost(post.id);
      }
    } catch (err) {
      // Revert on error
      setLiked(!liked);
      setLikeCount(post.like_count || 0);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      const res = await addComment(post.id, newComment.trim());
      const comment = res.data;
      comment.username = user.username;
      comment.full_name = user.full_name || user.username;
      setComments((prev) => [...prev, comment]);
      setCommentCount((prev) => prev + 1);
      setNewComment('');
    } catch (err) {
      console.error('Error adding comment:', err);
    }
  };

  const handleAddReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim() || !replyingTo) return;
    try {
      const res = await addComment(post.id, replyContent.trim(), replyingTo.id);
      const reply = res.data;
      reply.username = user.username;
      reply.full_name = user.full_name || user.username;
      setComments((prev) => [...prev, reply]);
      setCommentCount((prev) => prev + 1);
      setReplyContent('');
      setReplyingTo(null);
    } catch (err) {
      console.error('Error adding reply:', err);
    }
  };

  const handleRepost = async () => {
    try {
      if (reposted) {
        setReposted(false);
        setRepostCount((prev) => prev - 1);
        await unrepostPost(post.id);
      } else {
        setReposted(true);
        setRepostCount((prev) => prev + 1);
        await repostPost(post.id);
      }
    } catch (err) {
      setReposted(!reposted);
    }
  };

  const handleBookmark = async () => {
    try {
      if (bookmarked) {
        setBookmarked(false);
        await unbookmarkPost(post.id);
      } else {
        setBookmarked(true);
        await bookmarkPost(post.id);
      }
    } catch (err) {
      setBookmarked(!bookmarked);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment(commentId);
      // Remove comment and its replies
      setComments((prev) => prev.filter((c) => c.id !== commentId && c.parent_id !== commentId));
      setCommentCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };

  // Build threaded comments: top-level + replies grouped
  const topLevelComments = comments.filter((c) => !c.parent_id);
  const repliesByParent = {};
  comments
    .filter((c) => c.parent_id)
    .forEach((c) => {
      if (!repliesByParent[c.parent_id]) repliesByParent[c.parent_id] = [];
      repliesByParent[c.parent_id].push(c);
    });

  const getPostTypeBadge = () => {
    if (post.post_type === 'announcement')
      return <span className="post-type-badge announcement">Announcement</span>;
    if (post.post_type === 'audition')
      return <span className="post-type-badge audition">Audition Notice</span>;
    if (post.post_type === 'show') return <span className="post-type-badge show">Show Promo</span>;
    return null;
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `${weeks}w`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo`;
    return `${Math.floor(months / 12)}y`;
  };

  const CommentItem = ({ comment, isReply }) => (
    <div className={`comment ${isReply ? 'comment-reply' : ''}`}>
      <div className="comment-header">
        <Link to={`/profile/${comment.user_id}`} className="comment-avatar-link">
          <div className="comment-avatar">
            {(comment.full_name || comment.username)?.charAt(0).toUpperCase()}
          </div>
        </Link>
        <div className="comment-body">
          <div className="comment-bubble">
            <Link to={`/profile/${comment.user_id}`} className="comment-author">
              {comment.full_name || comment.username}
            </Link>
            <div className="comment-content">{comment.content}</div>
          </div>
          <div className="comment-meta">
            <span className="comment-time">{timeAgo(comment.created_at)}</span>
            {!isReply && (
              <button className="comment-reply-btn" onClick={() => setReplyingTo(comment)}>
                Reply
              </button>
            )}
            {user && comment.user_id === user.id && (
              <button
                className="comment-delete-btn"
                onClick={() => handleDeleteComment(comment.id)}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="post">
      {/* SHO-20: repost attribution. Server returns repost_by_* fields when
          the feed item came in via post_reposts; the original post body
          renders below as normal. */}
      {post.repost_by_username && post.repost_by_user_id !== post.user_id && (
        <div className="post-repost-banner">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <polyline points="17 1 21 5 17 9" />
            <path d="M3 11V9a4 4 0 0 1 4-4h14" />
            <polyline points="7 23 3 19 7 15" />
            <path d="M21 13v2a4 4 0 0 1-4 4H3" />
          </svg>
          <span>
            <Link
              to={`/profile/${post.repost_by_user_id}`}
              className="post-repost-author"
            >
              {post.repost_by_full_name || post.repost_by_username}
            </Link>{' '}
            reposted
          </span>
        </div>
      )}
      <div className="post-header">
        <Link to={`/profile/${post.user_id}`} className="post-avatar-link">
          <div className="post-avatar">
            {post.profile_image ? (
              <img src={getMediaUrl(post.profile_image)} alt="" />
            ) : (
              post.username?.charAt(0).toUpperCase()
            )}
          </div>
        </Link>
        <div className="post-user-info">
          <div className="post-name-row">
            <Link to={`/profile/${post.user_id}`} className="post-name">
              {post.full_name || post.username}
            </Link>
            {getPostTypeBadge()}
          </div>
          {post.headline && <div className="post-headline">{post.headline}</div>}
          {!post.headline && post.job_title && (
            <div className="post-headline">{post.job_title}</div>
          )}
          <div className="post-time">
            {timeAgo(post.created_at)}
            {displayEditedAt && (
              <span
                className="post-edited-indicator"
                title={`Edited ${new Date(displayEditedAt).toLocaleString()}`}
              >
                {' '}
                · edited
              </span>
            )}
          </div>
        </div>
        {user && post.user_id !== user.id && connectionState === 'none' && (
          <button
            className="post-connect-btn-inline"
            onClick={() => setShowConnectModal(true)}
          >
            + Connect
          </button>
        )}
        {user && post.user_id !== user.id && connectionState === 'pending' && (
          <button className="post-connect-btn-inline pending" disabled>
            Pending
          </button>
        )}
        {user && post.user_id === user.id && (
          <div className="post-owner-actions">
            <button
              className="post-edit-btn"
              onClick={handleStartEdit}
              title="Edit post"
              aria-label="Edit post"
              disabled={isEditing}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            <button className="post-delete-btn" onClick={handleDelete} title="Delete post">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </div>
        )}
      </div>
      {isEditing ? (
        <div className="post-edit-form">
          <textarea
            className="post-edit-textarea"
            value={editDraft}
            onChange={(e) => setEditDraft(e.target.value)}
            rows={4}
            autoFocus
            aria-label="Edit post content"
          />
          {editError && <div className="post-edit-error">{editError}</div>}
          <div className="post-edit-actions">
            <button
              type="button"
              className="post-edit-cancel"
              onClick={handleCancelEdit}
              disabled={editSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="post-edit-save"
              onClick={handleSubmitEdit}
              disabled={editSaving || !editDraft.trim()}
            >
              {editSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <div className="post-content">{displayContent}</div>
      )}

      {/* Legacy single image */}
      {post.image_url && (!post.media || post.media.length === 0) && (
        <img
          src={getMediaUrl(post.image_url)}
          alt="Post"
          className="post-image"
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            openLightbox({ type: 'image', url: getMediaUrl(post.image_url) });
          }}
        />
      )}

      {/* Media Gallery */}
      {post.media && post.media.length > 0 && (
        <div className={`post-media-gallery gallery-${Math.min(post.media.length, 5)}`}>
          {post.media.map((item, index) => (
            <div key={item.id || index} className="post-media-item">
              {item.media_type === 'image' ? (
                <img
                  src={getMediaUrl(item.url)}
                  alt={`Post media ${index + 1}`}
                  className="post-media-image"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openLightbox({ type: 'image', url: getMediaUrl(item.url) });
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : (
                <div
                  className="post-media-video"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openLightbox({
                      type: 'video',
                      embedUrl: item.embed_url || getMediaUrl(item.url),
                      isEmbed: !!item.embed_url,
                    });
                  }}
                >
                  {item.embed_url ? (
                    <iframe
                      src={item.embed_url}
                      title={`Video ${index + 1}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ pointerEvents: 'none' }}
                    />
                  ) : (
                    <video
                      src={getMediaUrl(item.url)}
                      className="post-media-video-player"
                      preload="metadata"
                    />
                  )}
                </div>
              )}
              {item.media_type === 'image' && (
                <div className="post-media-fallback" style={{ display: 'none' }}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    width="24"
                    height="24"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <span>Image unavailable</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="post-actions">
        <button
          className={`post-action-btn ${liked ? 'liked' : ''}`}
          onClick={handleLike}
          aria-label={liked ? 'Unlike' : 'Like'}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill={liked ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
          {likeCount > 0 && <span className="post-action-count">{likeCount}</span>}
          <span className="post-action-label">{likeCount === 1 ? 'Like' : 'Likes'}</span>
        </button>
        <button
          className={`post-action-btn ${showComments ? 'active' : ''}`}
          onClick={handleToggleComments}
          aria-label="Comments"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          {commentCount > 0 && <span className="post-action-count">{commentCount}</span>}
          <span className="post-action-label">{commentCount === 1 ? 'Comment' : 'Comments'}</span>
        </button>
        <button
          className={`post-action-btn ${reposted ? 'reposted' : ''}`}
          onClick={handleRepost}
          aria-label={reposted ? 'Undo repost' : 'Repost'}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="17 1 21 5 17 9" />
            <path d="M3 11V9a4 4 0 0 1 4-4h14" />
            <polyline points="7 23 3 19 7 15" />
            <path d="M21 13v2a4 4 0 0 1-4 4H3" />
          </svg>
          {repostCount > 0 && <span className="post-action-count">{repostCount}</span>}
          <span className="post-action-label">Repost</span>
        </button>
        <button
          className={`post-action-btn ${bookmarked ? 'bookmarked' : ''}`}
          onClick={handleBookmark}
          aria-label={bookmarked ? 'Remove bookmark' : 'Bookmark'}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill={bookmarked ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
          </svg>
          <span className="post-action-label">Save</span>
        </button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="post-comments" ref={commentsRef}>
          {topLevelComments.map((comment) => (
            <div key={comment.id}>
              <CommentItem comment={comment} isReply={false} />
              {/* Replies */}
              {repliesByParent[comment.id] &&
                repliesByParent[comment.id].map((reply) => (
                  <CommentItem key={reply.id} comment={reply} isReply={true} />
                ))}
              {/* Reply input */}
              {replyingTo?.id === comment.id && (
                <form className="comment-reply-form" onSubmit={handleAddReply}>
                  <div className="comment-reply-indicator">
                    Replying to <strong>{comment.full_name || comment.username}</strong>
                    <button
                      type="button"
                      className="comment-reply-cancel"
                      onClick={() => setReplyingTo(null)}
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="comment-input-container">
                    <input
                      ref={replyInputRef}
                      type="text"
                      className="comment-input"
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="Write a reply..."
                    />
                    <button
                      type="submit"
                      className="comment-submit-btn"
                      disabled={!replyContent.trim()}
                    >
                      Reply
                    </button>
                  </div>
                </form>
              )}
            </div>
          ))}

          {/* New comment input */}
          {user && (
            <form onSubmit={handleAddComment} className="comment-input-container">
              <input
                type="text"
                className="comment-input"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
              />
              <button type="submit" className="comment-submit-btn" disabled={!newComment.trim()}>
                Post
              </button>
            </form>
          )}
        </div>
      )}

      {/* SHO-19: shared ConnectModal — same markup Network/Profile use, so
          the Connect flow looks identical wherever it's triggered. */}
      {showConnectModal &&
        createPortal(
          <ConnectModal
            person={{
              id: post.user_id,
              full_name: post.full_name,
              username: post.username,
              profile_image: post.profile_image,
              job_title: post.job_title,
            }}
            isOpen={showConnectModal}
            onClose={() => setShowConnectModal(false)}
            onSent={() => setConnectionState('pending')}
          />,
          document.body
        )}

      {/* Lightbox — rendered via portal at document root */}
      {lightbox &&
        createPortal(
          <div className="post-lightbox-overlay" onClick={() => setLightbox(null)}>
            <button
              className="post-lightbox-close"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox(null);
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <div className="post-lightbox-content" onClick={(e) => e.stopPropagation()}>
              {lightbox.type === 'image' ? (
                <img src={lightbox.url} alt="Full view" className="post-lightbox-image" />
              ) : (
                <div className="post-lightbox-video">
                  {lightbox.isEmbed ? (
                    <iframe
                      src={lightbox.embedUrl}
                      title="Video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <video
                      src={lightbox.embedUrl}
                      controls
                      autoPlay
                      className="post-lightbox-video-player"
                    />
                  )}
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

// Memoized so Feed re-renders don't re-render every Post in the list.
// Stable prop identity is required for memo to actually skip work; Feed
// wraps `loadFeed`/etc. in useCallback to provide it.
export default React.memo(Post);
